import type { CSSProperties, ReactNode } from "react";
import {
  Button,
  Cluster,
  Diagnostic,
  EmptyState,
  Kicker,
  Note,
  Panel,
  Stack,
  Stat,
  StatRow,
  Status,
  Table,
  Title,
} from "@fwf/fwfa-design-system";

import { applyFilters, toFilterable } from "../filters";
import type { FilterState } from "../filters";
import {
  formatBytes,
  formatIsoDate,
  formatIsoRelative,
  formatQuota,
  sumUsageValues,
  titleCase,
} from "../format";
import type { Actions, AdminData, Toasts } from "../hooks";
import { daytonaState } from "../state";
import { stateTone } from "../types";
import type { AdminDaytonaSandbox } from "../types";

export type SectionProps = {
  data: AdminData;
  filters: FilterState;
  actions: Actions;
  toasts: Toasts;
};

type DaytonaAction = "start" | "stop" | "delete";

/**
 * Toast wording per action, so the verb an operator clicked is the verb they
 * read back. Start and stop report intent because Daytona answers before the
 * sandbox has actually settled into the new state.
 */
const ACTION_COPY: Record<DaytonaAction, { success: string; failure: string }> = {
  start: { success: "Sandbox starting", failure: "Could not start the sandbox" },
  stop: { success: "Sandbox stopping", failure: "Could not stop the sandbox" },
  delete: { success: "Sandbox deleted", failure: "Could not delete the sandbox" },
};

/** Sub-lines inside a cell sit tighter than the page-level stack rhythm. */
const CELL_GAP = { "--fwfa-stack-gap": "var(--fwfa-space-1)" } as CSSProperties;

/** Compact buttons read as one control group at the tighter cluster gap. */
const ACTION_GAP = { "--fwfa-cluster-gap": "var(--fwfa-space-2)" } as CSSProperties;

function SectionHeading({ children }: { children?: ReactNode }) {
  return (
    <div className="fwfa-section-header">
      <div>
        <Kicker>Workspace provider</Kicker>
        <Title as="h2" className="fwfa-title--section">
          Daytona sandboxes
        </Title>
      </div>
      {children}
    </div>
  );
}

export function DaytonaSection({ data, filters, actions }: SectionProps) {
  const daytona = data.daytona;
  const notice = data.notices.daytona;

  // Absent and quiet means Daytona is simply not part of this deployment, so it
  // should not take up space on the page at all.
  if (!daytona && !notice) return null;

  if (!daytona) {
    return (
      <Panel>
        <Stack>
          <SectionHeading />
          <Note variant="warn">{notice}</Note>
        </Stack>
      </Panel>
    );
  }

  const runAction = (sandbox: AdminDaytonaSandbox, action: DaytonaAction) => {
    void actions.run({
      key: `daytona:${sandbox.sandboxId}:${action}`,
      url: `/api/admin/daytona/sandboxes/${action}`,
      body: { sandboxId: sandbox.sandboxId },
      // Start and stop are reversible in one click, so they no longer interrupt.
      // Delete is not, so it keeps a confirm.
      confirm:
        action === "delete"
          ? {
              title: "Delete this sandbox?",
              body: `${sandbox.name} will be removed. This cannot be undone.`,
              confirmLabel: "Delete sandbox",
              danger: true,
            }
          : undefined,
      successTitle: ACTION_COPY[action].success,
      successMessage: sandbox.name,
      failureTitle: ACTION_COPY[action].failure,
    });
  };

  // Quota is reported per region; the strip shows the account total, so the
  // per-region rows are summed rather than shown one by one.
  const regions = daytona.usage?.regionUsage ?? [];
  const cpuUsed = sumUsageValues(regions.map((region) => region.currentCpuUsage));
  const cpuQuota = sumUsageValues(regions.map((region) => region.totalCpuQuota));
  const memoryUsed = sumUsageValues(regions.map((region) => region.currentMemoryUsage));
  const memoryQuota = sumUsageValues(regions.map((region) => region.totalMemoryQuota));
  const diskUsed = sumUsageValues(regions.map((region) => region.currentDiskUsage));
  const diskQuota = sumUsageValues(regions.map((region) => region.totalDiskQuota));

  const sandboxes = applyFilters(daytona.sandboxes, filters, (sandbox) =>
    toFilterable(daytonaState(sandbox), "n/a", [
      sandbox.sandboxId,
      sandbox.name,
      sandbox.state,
      sandbox.snapshot,
      sandbox.target,
    ]),
  );

  return (
    <Panel>
      <Stack>
        <SectionHeading>
          <p className="fwfa-subtitle fwfa-subtitle--sm">
            Target <span className="font-mono">{daytona.target}</span> · Snapshot{" "}
            <span className="font-mono">{daytona.snapshot}</span>
          </p>
        </SectionHeading>

        {daytona.usage ? (
          <StatRow compact>
            <Stat value={daytona.sandboxes.length} label="Sandboxes" />
            <Stat value={formatQuota(cpuUsed, cpuQuota, " vCPU")} label="CPU allocation" />
            <Stat value={formatQuota(memoryUsed, memoryQuota, " GiB")} label="Memory allocation" />
            <Stat value={formatQuota(diskUsed, diskQuota, " GiB")} label="Disk allocation" />
            <Stat
              value={formatQuota(
                daytona.usage.currentSnapshotUsage,
                daytona.usage.totalSnapshotQuota,
              )}
              label="Snapshots"
            />
          </StatRow>
        ) : (
          <Note variant="warn">Usage: {daytona.usageMessage || "not available"}</Note>
        )}

        {daytona.sandboxes.length === 0 && (
          <EmptyState title="No Daytona sandboxes">
            Nothing has been provisioned against this target yet.
          </EmptyState>
        )}

        {daytona.sandboxes.length > 0 && sandboxes.length === 0 && (
          <EmptyState title="No sandboxes match these filters">
            Clear the search or the state filter to see the full list.
          </EmptyState>
        )}

        {sandboxes.length > 0 && (
          <Table reflow>
            <thead>
              <tr>
                <th scope="col">Sandbox</th>
                <th scope="col">State</th>
                <th scope="col">Resources</th>
                <th scope="col">Live usage</th>
                <th scope="col">Activity</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sandboxes.map((sandbox) => {
                const state = daytonaState(sandbox);
                const metrics = sandbox.metrics;
                const busy = (action: DaytonaAction) =>
                  actions.isPending(`daytona:${sandbox.sandboxId}:${action}`);

                return (
                  <tr key={sandbox.sandboxId}>
                    <td data-label="Sandbox">
                      <Stack style={CELL_GAP}>
                        <span className="font-mono text-ink">{sandbox.name}</span>
                        {sandbox.name !== sandbox.sandboxId && (
                          <span className="fwfa-subtitle fwfa-subtitle--sm font-mono">
                            {sandbox.sandboxId}
                          </span>
                        )}
                        <span className="fwfa-subtitle fwfa-subtitle--sm">
                          {sandbox.target || "default target"} ·{" "}
                          {sandbox.snapshot || "default snapshot"}
                        </span>
                      </Stack>
                    </td>

                    <td data-label="State">
                      <Stack style={CELL_GAP}>
                        <Status tone={stateTone(state)} live={state === "running"}>
                          {titleCase(sandbox.state)}
                        </Status>
                        {sandbox.errorReason && <Diagnostic>{sandbox.errorReason}</Diagnostic>}
                      </Stack>
                    </td>

                    <td data-label="Resources">
                      <Stack style={CELL_GAP}>
                        <span>{sandbox.cpu === null ? "—" : `${sandbox.cpu} vCPU`}</span>
                        <span>{sandbox.memory === null ? "—" : `${sandbox.memory} GiB RAM`}</span>
                        <span>{sandbox.disk === null ? "—" : `${sandbox.disk} GiB disk`}</span>
                        <span className="fwfa-subtitle fwfa-subtitle--sm">
                          auto-stop{" "}
                          {sandbox.autoStopInterval === null
                            ? "off"
                            : `${sandbox.autoStopInterval}m`}
                        </span>
                      </Stack>
                    </td>

                    <td data-label="Live usage">
                      {metrics ? (
                        <Stack style={CELL_GAP}>
                          <span>
                            {metrics.cpuUsedPct === null
                              ? "—"
                              : `${metrics.cpuUsedPct.toFixed(1)}% CPU`}
                          </span>
                          <span>
                            {formatBytes(metrics.memUsed)} / {formatBytes(metrics.memTotal)} RAM
                          </span>
                          <span>
                            {formatBytes(metrics.diskUsed)} / {formatBytes(metrics.diskTotal)} disk
                          </span>
                          <span className="fwfa-subtitle fwfa-subtitle--sm">
                            sample {formatIsoRelative(metrics.timestamp)}
                          </span>
                        </Stack>
                      ) : (
                        <span className="fwfa-subtitle fwfa-subtitle--sm">
                          No live sample while stopped
                        </span>
                      )}
                    </td>

                    <td data-label="Activity">
                      <Stack style={CELL_GAP}>
                        <span>{formatIsoRelative(sandbox.lastActivityAt)} last active</span>
                        <span className="fwfa-subtitle fwfa-subtitle--sm">
                          created {formatIsoDate(sandbox.createdAt)}
                        </span>
                      </Stack>
                    </td>

                    <td data-label="Actions">
                      <Cluster style={ACTION_GAP}>
                        {state === "stopped" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("start")}
                            disabled={busy("start")}
                            onClick={() => runAction(sandbox, "start")}
                          >
                            Start
                          </Button>
                        )}
                        {state === "running" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("stop")}
                            disabled={busy("stop")}
                            onClick={() => runAction(sandbox, "stop")}
                          >
                            Stop
                          </Button>
                        )}
                        {state !== "terminal" && (
                          <Button
                            variant="secondary"
                            compact
                            loading={busy("delete")}
                            disabled={busy("delete")}
                            onClick={() => runAction(sandbox, "delete")}
                          >
                            Delete
                          </Button>
                        )}
                      </Cluster>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Stack>
    </Panel>
  );
}
